/* The mobile bottom sheet: geometry and gesture, and nothing else.
 *
 * Written for Map (ADR 0012) and extracted here when Detail wanted the same
 * thing (ADR 0013). It is a container in the same way the tab bar is, so ADR
 * 0007 principle 3 is satisfied by sharing it rather than by refusing to have
 * one — every *control* inside a sheet still lives in exactly one place.
 *
 * What it knows: how far down to sit, and which finger movements mean a change
 * of detent. What it never learns: what is inside it. The body is slotted.
 *
 * Callers describe the sheet with two properties:
 *
 *   detents  a table of name -> cover: px when > 1, a fraction of the host's
 *            height otherwise, or 'content' to be sized by what is slotted in
 *   ladder   the rungs a drag can commit to, ordered from lowest to highest
 *
 * A state that is NOT on the ladder is **modal**: only a downward drag acts on
 * it, and it emits `dismiss` rather than a detent name. That is Map's `detail`,
 * and expressing it as "off the ladder" is what lets both callers hand over one
 * static table instead of swapping properties as the state changes.
 *
 * 'content' inverts the pane's usual clamp for that one state, so it is only
 * sane for a modal one: the sheet takes the content's height (within `bounds`)
 * instead of the content taking the sheet's. The resolved cover cannot be known
 * until the content has laid out, so `sheet-measured` fires when it changes —
 * Map's camera has to wait for it.
 *
 * Four things here are easy to undo by accident and are commented where they
 * happen: why the transform is on a fixed-height host, why the pane is clamped
 * rather than flexed, why pointer capture goes on `.body` and not on the host,
 * and why taking a downward drag off a scroller needs a non-passive
 * `preventDefault`. Three of the four were reasoned wrong first and corrected
 * against a real device.
 */

import { LitElement, html, css } from 'lit';
import { customElement, property, query } from 'lit/decorators.js';
import { shadowReset } from '../../design/base.js';
import { GESTURE_ATTR } from '../../core/gestures.js';

/** The handle strip, and the first thing in the sheet at every detent. */
export const GRAB_H = 24;

/** Past this much travel a release commits to the neighbouring rung. */
const COMMIT_PX = 44;
/** A press shorter and stiller than this is a tap, not an abandoned drag. */
const TAP_PX = 6;
const TAP_MS = 400;

@customElement('g-sheet')
export class GSheet extends LitElement {
  static override styles = [
    shadowReset,
    css`
      /* Full height, translated down — NOT a sheet whose height animates.
         Animating height would relayout the whole body on every frame of every
         drag, and Map's body is a 521-row scroller. With a constant height the
         rows below the fold simply stay below it (and skip painting entirely,
         see place-row); the only thing moving is a transform on the
         compositor. */
      :host {
        position: absolute;
        left: 0;
        right: 0;
        bottom: 0;
        z-index: 2;
        display: flex;
        flex-direction: column;
        height: 100%;
        transform: translateY(var(--drop, 100%));
      }
      /* The transition arrives with the first measurement, not before: the host
         has no box until it is laid out, so --drop starts at 100% (off-screen)
         and an eager transition would slide the sheet up on every mount. */
      :host([ready]) {
        transition: transform 300ms cubic-bezier(0.32, 0.72, 0, 1);
      }
      /* Nothing to interpolate while a finger is setting the value directly. */
      :host([dragging]) {
        transition: none;
      }
      /* The document-level reduced-motion rule cannot cross this shadow root. */
      @media (prefers-reduced-motion: reduce) {
        :host {
          transition: none;
        }
      }

      /* The body IS the sheet — handle, band and pane all sit on it, so it
         carries the surface, the radius and the lift at every detent. It stays
         full-height: anything shorter would leave a gap under the sheet
         mid-drag, through which whatever the sheet covers would show. */
      .body {
        position: relative;
        flex: 1;
        min-height: 0;
        display: flex;
        flex-direction: column;
        border-radius: var(--radius-lg) var(--radius-lg) 0 0;
        background: var(--surface);
        box-shadow: 0 -2px 16px rgb(0 0 0 / 0.18);
      }

      /* Handle, the caller's "end" control and the caller's band, as one block —
         so the pane's clamp can be measured from a single box however much of
         it the caller chose to fill. */
      .head {
        position: relative;
        flex: none;
      }
      /* The drag target, and the tap target that makes every transition
         reachable without a gesture (ADR 0010 §2). touch-action:none because
         there is nothing to scroll here and the browser must not claim the
         vertical axis. */
      .grab {
        flex: none;
        display: grid;
        place-items: center;
        height: var(--grab-h);
        touch-action: none;
        cursor: grab;
      }
      .grab::before {
        content: '';
        width: 36px;
        height: 4px;
        border-radius: var(--radius-full);
        background: var(--border);
      }
      /* Rides the handle strip rather than the band, so it is in the same place
         whether or not the caller slots a band at all. */
      /* display:block is not optional — a <slot> is display:contents by
         default, which has no box to position, so position:absolute is
         silently ignored and the slotted control lays out in flow instead.
         Measured: the head came out 60px tall rather than 24, and the pane's
         clamp was 36px short for the whole of a modal detent. */
      .end {
        display: block;
        position: absolute;
        top: 0;
        right: calc(var(--row-inset-r) + var(--space-2));
        z-index: 1;
      }

      /* Clamped to the visible band, NOT flexed to fill the body.
         The body is a full-height element translated down, so everything below
         its cover hangs off the bottom of the screen — a pane that flexed would
         put that much of its scroller out of reach at the end of the scroll.
         Map lived with ~7% of its list unreadable at the full detent for
         exactly this reason; a half-height sheet would lose half of it. */
      .pane {
        flex: 1;
        min-height: 0;
        max-height: var(--pane-max, none);
        display: flex;
        flex-direction: column;
      }
      ::slotted(*) {
        flex: 1;
        min-height: 0;
      }
      /* A content-sized state has to stop both boxes flexing, or the pane fills
         the full-height body and the child stretches with it — and then the
         height we measure is the screen's, not the content's. Set imperatively
         in measure() so the layout read that follows it sees the new rule. */
      :host([sized]) .pane {
        flex: none;
        overflow: hidden;
      }
      :host([sized]) ::slotted(*) {
        flex: none;
      }
    `,
  ];

  /** Cover per detent: px when > 1, a fraction, or 'content' to be sized by it. */
  @property({ attribute: false }) detents: Record<string, number | 'content'> = {};
  /** Fractions a 'content' cover is clamped between. */
  @property({ attribute: false }) bounds: { min: number; max: number } = { min: 0.3, max: 0.66 };
  /** The rungs a drag moves between, lowest cover first. */
  @property({ attribute: false }) ladder: string[] = [];
  @property({ reflect: true }) state = '';
  /** Mirrors an in-flight drag so the transition can stand down. */
  @property({ type: Boolean, reflect: true }) dragging = false;
  /** Set once the host has a box and a real detent to sit at. */
  @property({ type: Boolean, reflect: true }) ready = false;

  @query('.body') private bodyEl?: HTMLElement;
  @query('.head') private headEl?: HTMLElement;
  @query('.pane') private paneEl?: HTMLElement;

  /** Height of the area the sheet sits over, and the basis of every detent. */
  private areaH = 0;
  private headH = GRAB_H;
  /** Natural height of the slotted content, for a 'content' detent. */
  private contentH = 0;
  /** Last cover reported, so sheet-measured fires on change and not per frame. */
  private reported = -1;
  private observer?: ResizeObserver;

  // Drag bookkeeping. Null pointer means nothing is armed.
  private pointer: number | null = null;
  /** Started on the handle, which drags in both directions at any detent. */
  private fromGrab = false;
  private startY = 0;
  private startDrop = 0;
  private startedAt = 0;
  private moved = 0;

  override connectedCallback(): void {
    super.connectedCallback();
    // Vertical is this component's axis (ADR 0013): the frame owns the page's,
    // the sheet owns its own band. Saying so here is what keeps a caller on a
    // refreshable workspace from pulling the workspace as the sheet comes down.
    this.setAttribute(GESTURE_ATTR, '');
    this.observer = new ResizeObserver(() => this.measure());
    this.observer.observe(this);
    this.measure();
  }

  override firstUpdated(): void {
    this.bodyEl?.addEventListener('touchmove', this.onTouchMove, { passive: false });
    // The head's height is the caller's, not ours — a slotted band can be any
    // height and Map hides its own in one state — so the pane's clamp is
    // measured rather than assumed.
    if (this.headEl) this.observer?.observe(this.headEl);
    // A content-sized detent moves when its content does.
    if (this.paneEl) this.observer?.observe(this.paneEl);
  }

  override disconnectedCallback(): void {
    super.disconnectedCallback();
    this.observer?.disconnect();
    this.bodyEl?.removeEventListener('touchmove', this.onTouchMove);
  }

  override updated(changed: Map<string, unknown>): void {
    // measure(), not settle(): the pane's clamp is derived from the state's
    // cover, so it goes stale the moment the state moves.
    if (changed.has('state')) this.reported = -1;
    if (changed.has('state') || changed.has('detents')) this.measure();
  }

  /**
   * Re-read the geometry now and return the current state's cover.
   *
   * For a content-sized detent the caller knows when the content changed and the
   * ResizeObserver does not — swapping one venue for another of the same height
   * resizes nothing. So Map calls this once the new content has rendered, rather
   * than waiting for an observation that may never come.
   */
  remeasure(): number {
    this.measure();
    return this.coverFor(this.state);
  }

  // ── geometry ───────────────────────────────────────────────

  private measure(): void {
    const h = this.offsetHeight;
    if (h) this.areaH = h;
    this.style.setProperty('--grab-h', `${GRAB_H}px`);
    this.headH = this.headEl?.offsetHeight ?? GRAB_H;

    // The clamp and the content's height are mutually defining, so for a
    // content-sized state the clamp comes off, the height is read, and only
    // then is the clamp re-applied. All three happen in one task, so the
    // intermediate state is a forced reflow and never a painted frame.
    const sized = this.detents[this.state] === 'content';
    this.toggleAttribute('sized', sized);
    if (sized) {
      this.style.setProperty('--pane-max', 'none');
      this.contentH = this.paneEl?.offsetHeight ?? 0;
    }

    const cover = this.coverFor(this.state);
    this.style.setProperty('--pane-max', `${Math.max(0, cover - this.headH)}px`);
    this.settle();
    if (this.areaH && !this.ready) requestAnimationFrame(() => (this.ready = true));

    if (cover !== this.reported) {
      this.reported = cover;
      this.dispatchEvent(
        new CustomEvent('sheet-measured', { detail: cover, bubbles: true, composed: true }),
      );
    }
  }

  /**
   * How much of the screen the sheet covers, opaquely, in this state.
   *
   * Public because the thing underneath needs it: Map centres a selected pin in
   * the band that is still visible, and Detail sizes the player to it.
   */
  coverFor(state: string): number {
    const c = this.detents[state];
    if (c === undefined) return 0;
    if (c !== 'content') return c > 1 ? Math.round(c) : Math.round(this.areaH * c);
    const want = this.contentH ? this.contentH + this.headH : 0;
    if (!this.areaH) return Math.round(want);
    const lo = this.areaH * this.bounds.min;
    const hi = this.areaH * this.bounds.max;
    return Math.round(Math.min(Math.max(want, lo), hi));
  }

  /** How far down the sheet sits in each state. 0 is fully raised. */
  private dropFor(state: string): number {
    return this.areaH ? this.areaH - this.coverFor(state) : 0;
  }

  /** Off the ladder: a modal state, which only ever collapses. */
  private get modal(): boolean {
    return !this.ladder.includes(this.state);
  }

  private get rung(): number {
    return this.ladder.indexOf(this.state);
  }

  private get atLowest(): boolean {
    return this.rung === 0;
  }

  private get hasAbove(): boolean {
    return this.rung >= 0 && this.rung < this.ladder.length - 1;
  }

  private applyDrop(px: number): void {
    this.style.setProperty('--drop', `${px}px`);
  }

  /** Return to the drop the current state calls for. */
  private settle(): void {
    this.applyDrop(this.dropFor(this.state));
  }

  // ── gesture ────────────────────────────────────────────────

  private onGrab(path: EventTarget[]): boolean {
    return path.some((n) => n instanceof HTMLElement && n.classList.contains('grab'));
  }

  /**
   * Whether the scroller under the finger is at its top.
   *
   * `composedPath()` rather than a query, because the scroller lives in the
   * slotted component's shadow root and `event.target` retargets to this host —
   * the same trap ADR 0010 §3 recorded for the gesture it later removed. A body
   * with nothing scrollable in it answers true, which is what lets a modal
   * sheet arm anywhere on itself.
   */
  private atTop(path: EventTarget[]): boolean {
    const scroller = path.find(
      (n) => n instanceof HTMLElement && n.scrollHeight > n.clientHeight + 1,
    ) as HTMLElement | undefined;
    return !scroller || scroller.scrollTop <= 0;
  }

  /**
   * Whether a drag starting here should move the sheet at all.
   *
   * The handle always arms. The lowest rung arms anywhere, because it shows too
   * little for anything under the finger to want the gesture instead. Everything
   * else arms only at the top of its scroller, the same guard pull-to-refresh
   * uses (ADR 0010 §2).
   */
  private armed(e: PointerEvent): boolean {
    const path = e.composedPath();
    return this.onGrab(path) || this.atLowest || this.atTop(path);
  }

  /**
   * Whether this gesture belongs to the sheet rather than to the scroller.
   *
   * Consulted only by `onTouchMove`, which is the piece that actually takes the
   * gesture away from the browser. An earlier version relied on pointer events
   * alone and on the fact that a scroller at `scrollTop: 0` with
   * `overscroll-behavior: contain` has nowhere to go. Measured on the device,
   * that reasoning was wrong: Chrome claims the touch for the scroller anyway,
   * fires `pointercancel`, and the sheet never moves. Only a non-passive
   * `preventDefault` on the first qualifying `touchmove` stops it.
   */
  private claims(dy: number, path: EventTarget[]): boolean {
    if (this.onGrab(path)) return true;
    if (this.modal) return dy > 0;
    if (this.atLowest) return true;
    if (dy < 0 && !this.hasAbove) return false;
    return this.atTop(path);
  }

  /**
   * Take the touch from the browser before it decides to scroll with it.
   *
   * Bound imperatively rather than in the template: a declarative Lit listener
   * cannot opt out of passive, and `preventDefault` in a passive listener is
   * ignored (with a console warning, which is the tell if this ever regresses).
   */
  private onTouchMove = (e: TouchEvent): void => {
    if (this.pointer == null) return;
    const t = e.touches[0];
    if (!t) return;
    const dy = t.clientY - this.startY;
    if (Math.abs(dy) < TAP_PX) return;
    if (this.claims(dy, e.composedPath())) e.preventDefault();
  };

  private onDown = (e: PointerEvent): void => {
    if (this.pointer != null || !e.isPrimary || !this.armed(e)) return;
    this.pointer = e.pointerId;
    this.fromGrab = this.onGrab(e.composedPath());
    this.startY = e.clientY;
    this.startDrop = this.dropFor(this.state);
    this.startedAt = performance.now();
    this.moved = 0;
  };

  private onMove = (e: PointerEvent): void => {
    if (e.pointerId !== this.pointer) return;
    const dy = e.clientY - this.startY;
    this.moved = Math.max(this.moved, Math.abs(dy));
    if (!this.dragging) {
      if (this.moved < TAP_PX) return;
      // Off the handle, a drag toward a rung that does not exist is only ever a
      // scroll. Without this an upward flick at the top rung would capture the
      // pointer here, clamp against the raised extreme so nothing appeared to
      // happen, and take the scroll with it. The handle itself is exempt — it
      // has nothing under it and works in both directions.
      if (!this.fromGrab && !this.atLowest) {
        if ((dy < 0 && !this.hasAbove) || !this.armed(e)) {
          this.pointer = null;
          return;
        }
      }
      this.dragging = true;
      // Capture on the LISTENING element, not on the host. Capturing on the
      // host retargets every later pointer event to the host itself, and these
      // handlers sit on `.body` inside the shadow root — which is a descendant,
      // so it never sees them again. Measured on the device: the sheet froze
      // 7px into a 500px drag and `pointerup` never arrived to settle it.
      (e.currentTarget as HTMLElement).setPointerCapture(e.pointerId);
    }
    // Clamp to the reachable extremes so the sheet cannot be dragged past
    // either — no rubber-band, because both ends are real positions.
    const top = this.ladder[this.ladder.length - 1];
    const min = this.dropFor(this.modal ? this.state : (top ?? this.state));
    const max = this.dropFor(this.modal ? this.state : (this.ladder[0] ?? this.state));
    this.applyDrop(Math.min(Math.max(this.startDrop + dy, min), max));
  };

  private onUp = (e: PointerEvent): void => {
    if (e.pointerId !== this.pointer) return;
    const dy = e.clientY - this.startY;
    const wasDragging = this.dragging;
    const quick = performance.now() - this.startedAt < TAP_MS;
    this.pointer = null;
    this.dragging = false;
    const host = e.currentTarget as HTMLElement;
    if (host.hasPointerCapture(e.pointerId)) host.releasePointerCapture(e.pointerId);

    // A tap on the handle is the non-gesture path through every transition.
    if (!wasDragging && this.moved < TAP_PX && quick) {
      if (this.onGrab(e.composedPath())) this.tapGrab();
      else this.settle();
      return;
    }

    if (!wasDragging) return this.settle();
    if (this.modal) {
      // Only downward dismisses; an upward drag settles back rather than
      // promoting, because a modal sheet has no rung to promote to.
      if (dy > COMMIT_PX) this.emit('dismiss');
      else this.settle();
      return;
    }
    if (dy < -COMMIT_PX) this.step(1);
    else if (dy > COMMIT_PX) this.step(-1);
    else this.settle();
  };

  private onCancel = (e: PointerEvent): void => {
    if (e.pointerId !== this.pointer) return;
    this.pointer = null;
    this.dragging = false;
    this.settle();
  };

  /** The handle is a real control, so it answers the keys a button answers. */
  private onGrabKey = (e: KeyboardEvent): void => {
    if (e.key !== 'Enter' && e.key !== ' ') return;
    e.preventDefault();
    this.tapGrab();
  };

  /** Step up if there is anywhere to go, else all the way back down. */
  private tapGrab(): void {
    if (this.modal) return this.emit('dismiss');
    if (this.hasAbove) this.step(1);
    else this.emit(this.ladder[0] ?? this.state);
  }

  private step(delta: number): void {
    const next = this.ladder[Math.min(Math.max(this.rung + delta, 0), this.ladder.length - 1)];
    if (!next || next === this.state) return this.settle();
    this.emit(next);
  }

  /** The caller owns the state; this only ever asks. */
  private emit(next: string): void {
    this.dispatchEvent(
      new CustomEvent('sheet-state', { detail: next, bubbles: true, composed: true }),
    );
  }

  override render() {
    return html`
      <div
        class="body"
        @pointerdown=${this.onDown}
        @pointermove=${this.onMove}
        @pointerup=${this.onUp}
        @pointercancel=${this.onCancel}
      >
        <div class="head">
          <div
            class="grab"
            role="button"
            tabindex="0"
            aria-label=${this.hasAbove ? 'Expand' : 'Collapse'}
            @keydown=${this.onGrabKey}
          ></div>
          <slot name="end" class="end"></slot>
          <slot name="band"></slot>
        </div>
        <div class="pane"><slot></slot></div>
      </div>
    `;
  }
}

declare global {
  interface HTMLElementTagNameMap {
    'g-sheet': GSheet;
  }
}
